/**
 * Echeance de changement du mot de passe d'une entree (« Expires » de KeePass / KeePassXC) : l'utilisateur fixe la
 * date avant laquelle changer ce mot de passe (politique d'entreprise, compte partage, mot de passe provisoire), la
 * Sante du coffre previent avant et signale l'echeance passee. La date vit UNIQUEMENT dans les secrets chiffres
 * (`secrets.passwordExpiry`, `AAAA-MM-JJ`), jamais en metadonnee. Une regle de renouvellement (« tous les 90 jours »,
 * `secrets.passwordRenewDays`, chiffree de meme) arme l'echeance et la reporte a chaque changement du mot de passe : la
 * regle se compte depuis le dernier changement, comme la duree de vie maximale d'un mot de passe d'annuaire. Module pur,
 * zero E/S.
 */
import type { EntrySecrets } from '@klavox/crypto-core';
import { isDocumentExpired, normalizeDocumentDate } from './typed-fields.js';

/** Une echeance dans ce delai est signalee « bientot » : deux semaines pour choisir le moment du changement. */
export const PASSWORD_EXPIRY_WARNING_MS = 14 * 24 * 60 * 60 * 1000;

/** Echeances rapides de l'editeur (les delais de KeePassXC, semaines mises a part). */
export const PASSWORD_EXPIRY_PRESETS: readonly { readonly months: number; readonly label: string }[] = [
  { months: 1, label: '1 mois' },
  { months: 3, label: '3 mois' },
  { months: 6, label: '6 mois' },
  { months: 12, label: '1 an' },
];

/** Regles de renouvellement proposees par l'editeur, en jours (les durees usuelles des politiques de mot de passe). */
export const PASSWORD_RENEWAL_PRESETS: readonly { readonly days: number; readonly label: string }[] = [
  { days: 30, label: '30 jours' },
  { days: 60, label: '60 jours' },
  { days: 90, label: '90 jours' },
  { days: 180, label: '180 jours' },
  { days: 365, label: '365 jours' },
];

/** Borne d'une regle lue (import, serveur) : au-dela de dix ans, ce n'est plus une regle de renouvellement. */
export const PASSWORD_RENEWAL_MAX_DAYS = 3650;

const DAY_MS = 24 * 60 * 60 * 1000;

/** `expired` : echeance passee (fin du jour) ; `soon` : dans `PASSWORD_EXPIRY_WARNING_MS` ; `later` : plus loin. */
export type PasswordExpiryState = 'expired' | 'soon' | 'later';

/** Echeance de l'entree, normalisee ; `null` si absente ou illisible (serveur, import) : elle n'affirme rien. */
export function readPasswordExpiry(secrets: EntrySecrets): string | null {
  const raw = secrets['passwordExpiry'];
  return typeof raw === 'string' ? normalizeDocumentDate(raw) : null;
}

/** Etat d'une echeance a `now` ; `null` si la date est illisible. L'echeance court jusqu'a la fin de son jour. */
export function passwordExpiryState(date: string, now: number): PasswordExpiryState | null {
  if (normalizeDocumentDate(date) === null) return null;
  if (isDocumentExpired(date, now)) return 'expired';
  return isDocumentExpired(date, now + PASSWORD_EXPIRY_WARNING_MS) ? 'soon' : 'later';
}

/**
 * Date situee `months` mois apres le jour de `now` (UTC, comme les autres dates du coffre). Un jour absent du mois
 * d'arrivee recule au dernier jour de ce mois : le 31 janvier plus un mois tombe le 28 (ou 29) fevrier, pas en mars.
 */
export function passwordExpiryIn(months: number, now: number): string {
  const today = new Date(now);
  const year = today.getUTCFullYear();
  const month = today.getUTCMonth() + months;
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const target = new Date(Date.UTC(year, month, Math.min(today.getUTCDate(), lastDay)));
  return target.toISOString().slice(0, 10);
}

/** Regle de renouvellement de l'entree en jours ; `null` si absente ou hors bornes (entier de 1 a 3650) : elle n'arme rien. */
export function readPasswordRenewDays(secrets: EntrySecrets): number | null {
  const raw = secrets['passwordRenewDays'];
  return typeof raw === 'number' && Number.isInteger(raw) && raw >= 1 && raw <= PASSWORD_RENEWAL_MAX_DAYS ? raw : null;
}

/** Date situee `days` jours apres le jour de `now` (UTC, comme `passwordExpiryIn`) : des jours entiers, l'heure ne compte pas. */
export function passwordExpiryAfterDays(days: number, now: number): string {
  return new Date(now + days * DAY_MS).toISOString().slice(0, 10);
}

/**
 * Une regle de renouvellement sans echeance lisible s'arme : l'echeance est posee a `now` plus la regle (regle tout
 * juste choisie, entree creee avec elle). Sans mot de passe, rien a renouveler : rien n'est pose.
 */
export function armPasswordRenewal(secrets: EntrySecrets, now: number): EntrySecrets {
  const days = readPasswordRenewDays(secrets);
  const password = typeof secrets.password === 'string' ? secrets.password : '';
  if (days === null || password.length === 0 || readPasswordExpiry(secrets) !== null) return secrets;
  return { ...secrets, passwordExpiry: passwordExpiryAfterDays(days, now) };
}

/**
 * Secrets a enregistrer apres une modification : quand le mot de passe change alors que son echeance est passee ou
 * proche (`soon`), le changement l'acquitte et l'echeance est retiree -- sinon la Sante du coffre continuerait de
 * reclamer un changement deja fait. Une echeance lointaine reste (on a change plus tot que prevu, la regle tient), et
 * une echeance que `next` modifie lui-meme est un choix explicite : elle reste aussi. Avec une regle de renouvellement,
 * le changement REPORTE l'echeance a `now` plus la regle, quelle que soit sa distance : la regle court depuis le
 * dernier changement. Une regle sans echeance s'arme (`armPasswordRenewal`).
 */
export function settlePasswordExpiry(previous: EntrySecrets, next: EntrySecrets, now: number): EntrySecrets {
  const oldPassword = typeof previous.password === 'string' ? previous.password : '';
  const newPassword = typeof next.password === 'string' ? next.password : '';
  const expiry = readPasswordExpiry(next);
  if (expiry === null) return armPasswordRenewal(next, now);
  if (oldPassword === newPassword || expiry !== readPasswordExpiry(previous)) return next;
  const renewDays = readPasswordRenewDays(next);
  if (renewDays !== null && newPassword.length > 0) return { ...next, passwordExpiry: passwordExpiryAfterDays(renewDays, now) };
  if (passwordExpiryState(expiry, now) === 'later') return next;
  const { passwordExpiry: _settled, ...rest } = next;
  return rest;
}
